import "./Popover.css";
import {
  HTMLProps,
  PropsWithChildren,
  useRef,
  useState,
  ReactNode,
  useMemo,
} from "react";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  Placement,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
  useRole,
  useClick,
  FloatingFocusManager,
  FloatingRootContext,
} from "@floating-ui/react";
import { makeClickOrDispatch } from "components/Movables/util";

interface PopoverProps {
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  content?: ReactNode;
  placement?: Placement;
  directRef?: (props: Record<string, unknown>) => ReactNode;
  ignoreTransparent?: boolean;
  allowAudioClick?: boolean;
}

const useCursedClick = (
  context: FloatingRootContext,
  ignoreTransparent = false,
  allowAudioClick = false
) => {
  const props = useClick(context);
  const clickOrDispatch = makeClickOrDispatch();

  const reference = useMemo(
    () => ({
      ...props.reference,
      onClick(event: React.MouseEvent<HTMLElement>) {
        if (event.shiftKey) return;
        // special cause audio
        if (allowAudioClick) {
          const absPos = {
            x: Math.floor(event.clientX),
            y: Math.floor(event.clientY),
          };
          const audioElt = document
            .elementsFromPoint(absPos.x, absPos.y)
            .find(
              (el) => el.tagName.toLowerCase() === "audio"
            ) as HTMLAudioElement;
          if (audioElt) {
            if (audioElt.paused) {
              void audioElt.play();
            } else {
              audioElt.pause();
            }
            return;
          }
        }
        if (!ignoreTransparent) {
          return props.reference?.onClick?.(event);
        }
        const { allow, nextElt } = clickOrDispatch(
          context.elements.domReference,
          event.nativeEvent
        );
        if (allow) {
          props.reference?.onClick?.(event);
        } else if (nextElt) {
          nextElt.dispatchEvent(new MouseEvent("click", event.nativeEvent));
        }
      },
    }),
    [
      allowAudioClick,
      clickOrDispatch,
      context.elements.domReference,
      ignoreTransparent,
      props.reference,
    ]
  );

  return { reference };
};

const PopoverInternal = (
  props: PropsWithChildren<PopoverProps> &
    Omit<HTMLProps<HTMLDivElement>, "content">
) => {
  const [open_, setOpen_] = useState(false);
  const open = props.open ?? open_;
  const setOpen = props.setOpen ?? setOpen_;
  const arrowRef = useRef(null);
  const {
    content,
    placement = "top",
    directRef,
    ignoreTransparent,
    allowAudioClick,
    children,
    ...domProps
  } = props;

  const nodeId = useFloatingNodeId();
  const { context, refs, floatingStyles } = useFloating({
    nodeId,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      offset(5),
      flip({
        crossAxis: false,
      }),
      shift({
        padding: 5,
      }),
      arrow({ element: arrowRef }),
    ],
  });

  const click = useCursedClick(context, ignoreTransparent, allowAudioClick);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const show = open && content;

  return (
    <>
      {directRef ? (
        directRef({
          ref: refs.setReference,
          ...getReferenceProps(domProps),
        })
      ) : (
        <div
          className="popover-container"
          ref={refs.setReference}
          {...getReferenceProps(domProps)}
        >
          {children}
        </div>
      )}
      {show ? (
        <FloatingFocusManager context={context} modal={false}>
          <FloatingNode id={nodeId}>
            <FloatingPortal>
              <div
                className="popover game-root"
                ref={refs.setFloating}
                style={floatingStyles}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                {...getFloatingProps()}
              >
                {content}
                <FloatingArrow
                  className="popover-arrow"
                  ref={arrowRef}
                  context={context}
                />
              </div>
            </FloatingPortal>
          </FloatingNode>
        </FloatingFocusManager>
      ) : null}
    </>
  );
};

/** A popover that appears when the child is clicked. */
const Popover = (
  props: PropsWithChildren<PopoverProps> &
    Omit<HTMLProps<HTMLDivElement>, "content">
) => {
  // black magic... don't worry about it
  const parentId = useFloatingParentNodeId();

  if (parentId === null) {
    return (
      <FloatingTree>
        <PopoverInternal {...props} />
      </FloatingTree>
    );
  }

  return <PopoverInternal {...props} />;
};

export default Popover;
